<script setup>
import { ChevronDownIcon } from '@heroicons/vue/24/solid'
import { Link } from '@inertiajs/vue3'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import Mascot from '@/Components/Mascot.vue'
import Logo from '@/Components/Logo.vue'

defineProps({
  items: {
    type: Array,
    required: true,
  },
})
</script>

<template>
  <nav class="hidden lg:flex lg:items-center">
    <Link :href="route('index')" class="flex shrink-0 items-center">
      <Mascot class="h-8 w-auto lg:h-9" />
      <Logo class="ml-2 h-9 w-auto" alt="logo" />
    </Link>

    <ul class="ml-6 flex items-baseline space-x-4">
      <template v-for="item in items.filter((item) => item.visible)" :key="item.name">
        <li v-if="item.children">
          <Popover
            v-slot="{ open }"
            class="!focus:outline-none !focus-visible:outline-none !focus-visible:ring-none relative"
          >
            <PopoverButton
              as="div"
              :class="[
                item.current
                  ? 'border-link-hover font-bold text-link'
                  : 'border-transparent font-medium text-gray-500 hover:border-link-hover hover:text-link',
                'group flex cursor-pointer items-center border-b-2 p-1 text-sm',
              ]"
            >
              <span class="transition-all duration-300 ease-in-out">
                {{ item.name }}
              </span>

              <ChevronDownIcon
                :class="open ? 'rotate-180' : ''"
                class="ml-1 size-4 transition duration-300 ease-in-out group-hover:border-link-hover"
                aria-hidden="true"
              />
            </PopoverButton>

            <transition
              enter-active-class="transition duration-200 ease-out"
              enter-from-class="translate-y-1 opacity-0"
              enter-to-class="translate-y-0 opacity-100"
              leave-active-class="transition duration-150 ease-in"
              leave-from-class="translate-y-0 opacity-100"
              leave-to-class="translate-y-1 opacity-0"
            >
              <PopoverPanel
                class="absolute left-1/2 z-20 w-[200px] -translate-x-1/2 px-4 sm:px-0 lg:max-w-3xl"
              >
                <div class="rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5">
                  <div class="relative">
                    <Link
                      v-for="child in item.children"
                      :key="child.name"
                      class="flex py-2 transition-all duration-150 ease-in-out hover:border-l-4 hover:border-link-hover hover:text-link"
                      :rel="child.nofollow ? 'nofollow' : null"
                      :class="[
                        child.current
                          ? ' border-l-4 border-link font-bold text-link'
                          : 'font-medium text-gray-500',
                      ]"
                      :href="child.href"
                    >
                      <span class="pl-4 text-sm">{{ child.name }}</span>
                    </Link>
                  </div>
                </div>
              </PopoverPanel>
            </transition>
          </Popover>
        </li>

        <li v-else>
          <Link
            :key="item.name"
            :href="item.href"
            :rel="item.nofollow ? 'nofollow' : null"
            :class="[
              item.current
                ? 'border-link-hover font-bold text-link'
                : 'border-transparent font-medium text-gray-500 hover:border-link-hover hover:text-link',
              'group border-b-2 p-1 text-sm',
            ]"
          >
            <span class="transition-all duration-300 ease-in-out group-hover:scale-105">
              {{ item.name }}
            </span>
          </Link>
        </li>
      </template>
    </ul>
  </nav>
</template>
